import { z } from 'zod'
import { noteNameSchema } from './note.js'
import { scaleSchema } from './scale.js'

/**
 * Melody suggestion schemas. See `docs/superpowers/specs/2026-05-14-melody-suggestions-v1.md`.
 *
 * F.2.1: scale-constrained, single candidate, no section adaptation.
 */

export const melodyDurations = ['1n', '2n', '4n', '8n', '16n'] as const

const melodyDurationSchema = z.enum(melodyDurations).describe('Note duration — whole, half, quarter, eighth, sixteenth')

export type MelodyDuration = z.infer<typeof melodyDurationSchema>

export const melodyNoteSchema = z.object({
  pitch: noteNameSchema.describe('Note name in the scale, e.g., "C", "D#"'),
  octave: z.number().int().min(0).max(8).describe('Octave number; 3-5 typical for singable melodies'),
  duration: melodyDurationSchema,
  position: z.number().describe('Beats from start of section (0-indexed)'),
})

export type MelodyNote = z.infer<typeof melodyNoteSchema>

export const melodyCandidateSchema = z.object({
  notes: z.array(melodyNoteSchema).describe('Ordered melody notes'),
  rationale: z.string().describe('One-line music-theoretic justification of the candidate'),
})

export type MelodyCandidate = z.infer<typeof melodyCandidateSchema>

export const melodySuggestionResponseSchema = z.object({
  candidates: z.array(melodyCandidateSchema).describe('Generated melody candidates'),
  scale: scaleSchema.describe('Scale that was used to generate (echoed back for client confirmation)'),
  tokens: z.number().describe('Total tokens consumed by the LLM call'),
})

export type MelodySuggestionResponse = z.infer<typeof melodySuggestionResponseSchema>
